import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { BRAND } from "@/lib/brand";
import { LogoMark } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.name}`);
      nav(user.role === "admin" ? "/admin" : user.role === "shipper" ? "/shipper" : "/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-xd-bg text-white grid lg:grid-cols-2">
      <div className="hidden lg:block relative grain">
        <img src={BRAND.heroDually} alt="XD FREIGHT truck" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-xd-bg via-xd-bg/60 to-xd-bg/30" />
        <div className="absolute top-10 left-12"><LogoMark to="/" /></div>
        <div className="absolute bottom-12 left-12 right-12">
          <h2 className="font-heading text-4xl font-black tracking-tight">Find loads. Get paid.<br/><span className="text-xd-gold">Run your business.</span></h2>
        </div>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8"><LogoMark to="/" /></div>
          <h1 className="font-heading text-3xl font-black tracking-tight">Welcome back</h1>
          <p className="mt-2 text-xd-muted text-sm">Log in to your XD FREIGHT account.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email" className="text-xd-muted">Email</Label>
              <Input id="email" data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 bg-xd-surface border-xd-border h-11 rounded-xl" placeholder="you@company.com" />
            </div>
            <div>
              <Label htmlFor="password" className="text-xd-muted">Password</Label>
              <Input id="password" data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 bg-xd-surface border-xd-border h-11 rounded-xl" placeholder="••••••••" />
            </div>
            <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full h-11 bg-xd-blue hover:bg-xd-blueH text-white font-bold rounded-xl">
              {loading ? <Loader2 className="animate-spin" size={18} /> : "Log In"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-xd-muted text-center">New to XD? <Link to="/register" className="text-xd-gold font-semibold">Join XD</Link></p>
        </div>
      </div>
    </div>
  );
}
